import AgencyNavbar from "@/components/agency/AgencyNavbar";
import AgencyFooter from "@/components/agency/AgencyFooter";
import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Link } from "@/lib/router-compat";
import { ArrowRight, MapPin, Calendar } from "lucide-react";
import { SITE_ORIGIN, cities, citiesByCounty, cityPath, tradePath, trades } from "@/lib/localSeo";
import kingCountyHero from "@/assets/agency/king-county-websites.jpg";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      name: "King County Contractor Web Design and Local SEO",
      url: `${SITE_ORIGIN}/king-county-contractors`,
      provider: {
        "@type": "LocalBusiness",
        name: "jaik",
        url: SITE_ORIGIN,
        telephone: "+1-253-293-1550",
        address: { "@type": "PostalAddress", addressLocality: "Seattle", addressRegion: "WA", addressCountry: "US" },
      },
      areaServed: cities.map((c) => ({ "@type": "City", name: c.name })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "King County Contractors", item: `${SITE_ORIGIN}/king-county-contractors` },
      ],
    },
  ],
};

const KingCountyContractors = () => {
  return (
    <div className="min-h-screen bg-agency-bg font-sans text-agency-text">
      <SEOHead
        title="Contractor Web Design and Local SEO | King County & Western Washington | jaik"
        description="Contractor website design and local SEO across King County and western Washington. City pages for Seattle, Bellevue, Tacoma, Everett, Bellingham, Vancouver and more."
        canonical={`${SITE_ORIGIN}/king-county-contractors`}
        jsonLd={jsonLd}
      />
      <AgencyNavbar />

      <main>
        <section className="relative min-h-[650px] sm:min-h-[700px] flex items-end overflow-hidden">
          <img src={kingCountyHero} alt="Contractor work vans parked on a wet King County residential street" width={1536} height={960} className="absolute inset-0 h-full w-full object-cover object-[70%_center]" />
          <div className="absolute inset-0 bg-gradient-to-r from-agency-bg via-agency-bg/90 to-agency-bg/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-agency-bg via-transparent to-agency-bg/30" />
          <div className="container relative z-10 pb-14 pt-32 sm:pb-20 sm:pt-40">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
              <span className="font-sans text-xs text-agency-accent tracking-[0.2em] uppercase mb-4 block">King County, Washington</span>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-6 [text-wrap:balance]">
                Contractor Web Design and Local SEO Built for King County
              </h1>
              <p className="font-sans text-agency-text-muted text-lg leading-relaxed mb-8">
                 Websites and local SEO for trades serving Seattle, Bellevue, Kent and Auburn, plus city pages across western Washington.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/book" className="btn-jaik btn-jaik-primary">
                  <Calendar className="h-4 w-4" /> Book a call, get a free mockup
                </Link>
                <Link to="/pricing" className="btn-jaik btn-jaik-secondary">
                  Packages and pricing
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="pb-14 sm:pb-20">
          <div className="container">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">King County cities I build for</h2>
            <p className="font-sans text-agency-text-muted mb-7 max-w-2xl">
              Pick your city to see how the local market looks and what a site built for it includes.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cities
                .filter((c) => c.county === "King County")
                .map((city, i) => (
                  <motion.div
                    key={city.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: Math.min(i, 8) * 0.04, duration: 0.4 }}
                  >
                    <Link
                      to={cityPath(city)}
                      className="group block h-full bg-agency-surface border border-agency-border rounded-xl p-6 hover:border-agency-accent/40 transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="h-4 w-4 text-agency-accent" />
                        <h3 className="font-heading text-lg font-semibold">{city.name}, WA</h3>
                      </div>
                      <span className="font-sans text-xs text-agency-accent font-medium">{city.population} residents</span>
                      <p className="font-sans text-sm text-agency-text-muted leading-relaxed mt-3">{city.market.split(". ")[0]}.</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-agency-text">
                        Contractor web design in {city.name}
                        <ArrowRight className="h-3.5 w-3.5 text-agency-accent group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
            </div>

            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-16 mb-3">
              Western Washington cities I build for
            </h2>
            <p className="font-sans text-agency-text-muted mb-7 max-w-2xl">
              Outside King County I build the same kind of local pages across the Puget Sound region, the north sound, the Kitsap
              peninsula, the coast and southwest Washington.
            </p>
            <div className="space-y-7">
              {citiesByCounty()
                .filter((g) => g.county !== "King County")
                .map((group) => (
                  <div key={group.county}>
                    <h3 className="font-heading text-base font-semibold mb-3">{group.county}</h3>
                    <div className="flex flex-wrap gap-2.5">
                      {group.cities.map((city) => (
                        <Link
                          key={city.slug}
                          to={cityPath(city)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-agency-border bg-agency-surface/60 px-4 py-2 text-sm text-agency-text-muted hover:border-agency-accent/40 hover:text-agency-text transition-colors"
                        >
                          <MapPin className="h-3.5 w-3.5 text-agency-accent" /> Contractor web design in {city.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </section>

        <section className="pb-14 sm:pb-20">
          <div className="container">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">Trades I build for</h2>
            <p className="font-sans text-agency-text-muted mb-7 max-w-2xl">
              Each trade sells differently online, so each one gets its own page structure, keywords and lead capture.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {trades.map((t) => (
                <Link
                  key={t.slug}
                  to={tradePath(t)}
                  className="group bg-agency-surface/60 border border-agency-border rounded-xl px-5 py-4 hover:border-agency-accent/40 transition-colors"
                >
                  <span className="font-sans text-sm font-medium flex items-center justify-between gap-3">
                    {t.label} contractor web design
                    <ArrowRight className="h-4 w-4 text-agency-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                  <span className="block font-sans text-xs text-agency-text-muted mt-1.5 leading-relaxed">{t.buyer}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-agency-surface/30">
          <div className="container text-center">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-6">Own your corner of King County</h2>
            <p className="font-sans text-agency-text-muted text-lg mb-8 max-w-xl mx-auto">
              Most contractors here are fighting over one generic page. A site with real city and service pages behind it wins the searches
              they never target.
            </p>
            <Link to="/book" className="btn-jaik btn-jaik-primary btn-jaik-lg">
              <Calendar className="h-4 w-4" /> Book a call, get a free mockup <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <AgencyFooter />
    </div>
  );
};

export default KingCountyContractors;
